/* Dafodila Shoppable Videos — product page + home page
 * One custom element per section. Loaded only when the section has videos.
 */
(() => {
  if (customElements.get('dafodila-shoppable')) return;

  const ICON_SOUND_ON =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H2v6h4l5 4V5z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M19 5a10 10 0 0 1 0 14"/></svg>';
  const ICON_PAUSE =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="4.5" width="4" height="15" rx="1"/><rect x="14" y="4.5" width="4" height="15" rx="1"/></svg>';
  const ICON_PLAY =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4.5v15l13-7.5z"/></svg>';
  const ICON_SOUND_OFF =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H2v6h4l5 4V5z"/><path d="m23 9-6 6"/><path d="m17 9 6 6"/></svg>';

  class DafodilaShoppable extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.strip = this.querySelector('.ds__strip');
      this.thumbs = Array.from(this.querySelectorAll('.ds__thumb'));
      this.dialog = this.querySelector('.ds-modal');
      if (!this.strip || !this.dialog || !this.thumbs.length) return;

      this.items = this.thumbs.map((t) => ({
        full: t.dataset.full,
        poster: t.dataset.poster,
        product: t.dataset.product === undefined || t.dataset.product === '' ? -1 : Number(t.dataset.product),
      }));
      this.count = this.items.length;
      this.index = 0;
      this.soundOn = true;
      this.busy = false;
      this.dragEndAt = 0;
      this.visibleThumbs = new Set();
      this.autoNext = this.dataset.autonext === 'true';
      this.pageType = this.dataset.pageType || '';
      this.selected = {}; // productId -> chosen variantId

      try {
        this.products = JSON.parse(this.querySelector('[data-ds-products]').textContent) || [];
      } catch (e) {
        this.products = [];
      }

      this.ac = new AbortController();
      const sig = { signal: this.ac.signal };
      this.setupStrip(sig);
      this.setupModal(sig);

      // Coming back from the cart with the browser's Back button: reset the button.
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) {
          this.busy = false;
          this.renderCard();
        }
      }, sig);
    }

    disconnectedCallback() {
      if (this.ac) this.ac.abort();
      if (this.io) this.io.disconnect();
      if (this.dialog && this.dialog.open) this.dialog.close();
      this.stopProgress();
      this._ready = false;
    }

    /* ---------------- Thumbnail strip ---------------- */

    setupStrip(sig) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const saveData = navigator.connection && navigator.connection.saveData;

      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => this.open(i), sig);
      });

      // Thumbnail videos load and play only while visible on screen.
      if (!reduce && !saveData && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.visibleThumbs.add(entry.target);
                if (!this.dialog.open) this.playThumb(entry.target);
              } else {
                this.visibleThumbs.delete(entry.target);
                const v = entry.target.querySelector('.ds__thumb-video');
                if (v) v.pause();
              }
            });
          },
          { threshold: 0.6 }
        );
        this.thumbs.forEach((t) => this.io.observe(t));
      }

      const s = this.strip;
      const prev = this.querySelector('.ds__arrow--prev');
      const next = this.querySelector('.ds__arrow--next');
      const bar = this.querySelector('.ds__bar');

      // Width of one "page" = all cards that fit fully in view.
      const pageWidth = () => {
        const card = this.thumbs[0];
        const gap = parseFloat(getComputedStyle(s).columnGap) || 0;
        const step = card.offsetWidth + gap;
        const perView = Math.max(1, Math.floor((s.clientWidth + gap) / step));
        return perView * step;
      };

      const update = () => {
        const max = s.scrollWidth - s.clientWidth;
        const scrollable = max > 2;

        if (prev) {
          prev.hidden = !scrollable;
          prev.disabled = s.scrollLeft <= 2;
        }
        if (next) {
          next.hidden = !scrollable;
          next.disabled = s.scrollLeft >= max - 2;
        }

        if (bar) {
          bar.hidden = !scrollable;
          if (scrollable) {
            const w = Math.max(12, (s.clientWidth / s.scrollWidth) * 100);
            const x = (Math.min(Math.max(s.scrollLeft, 0), max) / max) * (100 - w);
            bar.style.setProperty('--ds-bar-w', w + '%');
            bar.style.setProperty('--ds-bar-x', x + '%');
          }
        }
      };

      if (prev) prev.addEventListener('click', () => s.scrollBy({ left: -pageWidth(), behavior: 'smooth' }), sig);
      if (next) next.addEventListener('click', () => s.scrollBy({ left: pageWidth(), behavior: 'smooth' }), sig);

      // Clicking the bar jumps to that part of the row.
      if (bar) {
        bar.addEventListener('click', (e) => {
          const rect = bar.getBoundingClientRect();
          const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
          s.scrollTo({ left: ratio * (s.scrollWidth - s.clientWidth), behavior: 'smooth' });
        }, sig);
      }

      let frame = null;
      s.addEventListener(
        'scroll',
        () => {
          if (frame) return;
          frame = requestAnimationFrame(() => {
            frame = null;
            update();
          });
        },
        { passive: true, signal: this.ac.signal }
      );

      window.addEventListener(
        'resize',
        () => {
          update();
          if (this.dialog.open) {
            this.measureCard();
            this.positionRail();
          }
        },
        sig
      );

      update();
    }

    playThumb(thumb) {
      const v = thumb.querySelector('.ds__thumb-video');
      if (!v) return;
      if (!v.getAttribute('src') && v.dataset.src) v.src = v.dataset.src;
      v.muted = true;
      const p = v.play();
      if (p && p.then) {
        p.then(() => v.classList.add('is-playing')).catch(() => {});
      }
    }

    pauseStrip() {
      this.thumbs.forEach((t) => {
        const v = t.querySelector('.ds__thumb-video');
        if (v) v.pause();
      });
    }

    resumeStrip() {
      this.visibleThumbs.forEach((t) => this.playThumb(t));
    }

    /* ---------------- Modal ---------------- */

    setupModal(sig) {
      const d = this.dialog;
      this.viewport = d.querySelector('.ds-modal__viewport');
      this.rail = d.querySelector('.ds-modal__rail');
      this.progress = d.querySelector('.ds-modal__progress');
      this.counter = d.querySelector('.ds-modal__count');
      this.tap = d.querySelector('.ds-modal__tap');
      this.soundBtn = d.querySelector('.ds-modal__sound');
      this.pauseBtn = d.querySelector('.ds-modal__pause');
      this.closeBtn = d.querySelector('.ds-modal__close');
      this.card = d.querySelector('.ds-card');
      this.buyBtn = d.querySelector('.ds-card__buy');
      this.msg = d.querySelector('.ds-card__msg');
      this.nowEl = d.querySelector('.ds-card__now');
      this.wasEl = d.querySelector('.ds-card__was');
      this.offEl = d.querySelector('.ds-card__off');
      this.cardImg = d.querySelector('.ds-card__img');
      this.cardTitle = d.querySelector('.ds-card__title');
      this.variantsEl = d.querySelector('.ds-card__variants');

      // Build slides (posters only — one shared <video> moves between slides)
      this.rail.innerHTML = '';
      this.progress.innerHTML = '';
      this.slides = this.items.map((item, i) => {
        const slide = document.createElement('div');
        slide.className = 'ds-slide';
        slide.dataset.index = String(i);
        if (item.poster) {
          const img = document.createElement('img');
          img.alt = '';
          img.decoding = 'async';
          img.dataset.src = item.poster;
          slide.appendChild(img);
        }
        this.rail.appendChild(slide);

        const seg = document.createElement('span');
        seg.className = 'ds-modal__seg';
        seg.appendChild(document.createElement('i'));
        this.progress.appendChild(seg);
        return slide;
      });
      this.segs = Array.from(this.progress.children);

      const v = document.createElement('video');
      v.setAttribute('playsinline', '');
      v.playsInline = true;
      v.preload = 'auto';
      v.loop = !this.autoNext;
      v.disablePictureInPicture = true;
      v.setAttribute('controlslist', 'nodownload noplaybackrate');
      this.video = v;

      this.preloader = document.createElement('video');
      this.preloader.muted = true;
      this.preloader.preload = 'auto';

      v.addEventListener('playing', () => {
        v.classList.add('is-ready');
        this.updateTap();
        this.preloadNext();
      }, sig);
      v.addEventListener('play', () => {
        this.updateTap();
        this.startProgress();
      }, sig);
      v.addEventListener('pause', () => {
        this.updateTap();
        this.stopProgress();
      }, sig);
      v.addEventListener('ended', () => {
        if (this.autoNext && this.index < this.count - 1) {
          this.go(this.index + 1);
        } else {
          this.updateTap();
        }
      }, sig);

      const togglePlay = () => {
        if (Date.now() - this.dragEndAt < 350) return;
        if (v.paused) {
          if (v.ended) v.currentTime = 0;
          this.play();
        } else {
          v.pause();
        }
      };
      this.tap.addEventListener('click', togglePlay, sig);
      if (this.pauseBtn) this.pauseBtn.addEventListener('click', togglePlay, sig);

      this.soundBtn.addEventListener('click', () => {
        this.soundOn = !this.soundOn;
        v.muted = !this.soundOn;
        if (this.soundOn && v.paused) this.play();
        this.updateSound();
      }, sig);

      this.closeBtn.addEventListener('click', () => d.close(), sig);

      d.addEventListener('cancel', (e) => {
        e.preventDefault();
        d.close();
      }, sig);
      d.addEventListener('close', () => this.onClosed(), sig);

      d.addEventListener('keydown', (e) => {
        if (e.target.closest && e.target.closest('.ds-card')) return;
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.go(this.index + 1);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.go(this.index - 1);
        }
      }, sig);

      // Clicking a neighbour slide moves to it; clicking empty space closes.
      this.viewport.addEventListener('click', (e) => {
        if (Date.now() - this.dragEndAt < 350) return;
        const slide = e.target.closest('.ds-slide');
        if (slide && !slide.classList.contains('is-active')) {
          this.go(Number(slide.dataset.index));
          return;
        }
        if (e.target === this.viewport || e.target === this.rail) d.close();
      }, sig);

      this.setupDrag(sig);

      this.variantsEl.addEventListener('click', (e) => {
        const chip = e.target.closest('.ds-card__chip');
        const product = this.currentProduct();
        if (!chip || !product) return;
        this.selected[product.id] = Number(chip.dataset.variantId);
        this.msg.textContent = '';
        this.renderCard();
      }, sig);

      this.buyBtn.addEventListener('click', () => this.buy(), sig);
    }

    setupDrag(sig) {
      const vp = this.viewport;
      let startX = 0;
      let startY = 0;
      let dx = 0;
      let active = false;
      let moved = false;
      let pid = null;

      vp.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.target.closest('.ds-card, .ds-modal__top, .ds-modal__side')) return;
        startX = e.clientX;
        startY = e.clientY;
        dx = 0;
        active = true;
        moved = false;
        pid = e.pointerId;
      }, sig);

      vp.addEventListener('pointermove', (e) => {
        if (!active || e.pointerId !== pid) return;
        dx = e.clientX - startX;
        const dy = e.clientY - startY;
        if (!moved) {
          if (Math.abs(dx) < 8) return;
          if (Math.abs(dy) > Math.abs(dx)) {
            active = false;
            return;
          }
          moved = true;
          vp.classList.add('is-dragging');
          this.rail.classList.add('is-dragging');
          try {
            vp.setPointerCapture(pid);
          } catch (err) {}
        }
        const atEdge = (this.index === 0 && dx > 0) || (this.index === this.count - 1 && dx < 0);
        this.positionRail(atEdge ? dx * 0.3 : dx);
      }, sig);

      const end = () => {
        if (!active) return;
        active = false;
        vp.classList.remove('is-dragging');
        this.rail.classList.remove('is-dragging');
        if (!moved) return;
        this.dragEndAt = Date.now();
        const slide = this.slides[this.index];
        const threshold = Math.min(80, slide.offsetWidth * 0.2);
        if (dx < -threshold && this.index < this.count - 1) this.go(this.index + 1);
        else if (dx > threshold && this.index > 0) this.go(this.index - 1);
        else this.positionRail();
      };

      vp.addEventListener('pointerup', end, sig);
      vp.addEventListener('pointercancel', end, sig);
    }

    open(i) {
      this.pauseStrip();
      this.syncVariantFromPage();
      this.msg.textContent = '';
      this.soundOn = true;
      this.updateSound();

      this.dialog.showModal();
      document.documentElement.classList.add('ds-lock');
      this.measureCard();

      // Jump to the clicked slide without the sliding animation.
      this.rail.classList.add('is-dragging');
      requestAnimationFrame(() => {
        this.go(i, true);
        void this.rail.offsetWidth;
        this.rail.classList.remove('is-dragging');
        this.nudge(i);
      });
    }

    // Mobile, first open only: briefly slide to reveal the next video, then back.
    nudge(i) {
      if (this.nudged || i >= this.count - 1) return;
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.nudged = true;
      setTimeout(() => {
        if (!this.dialog.open || this.index !== i || this.viewport.classList.contains('is-dragging')) return;
        this.positionRail(-70);
        setTimeout(() => {
          if (this.index === i && !this.viewport.classList.contains('is-dragging')) this.positionRail();
        }, 550);
      }, 1100);
    }

    onClosed() {
      const v = this.video;
      v.pause();
      v.removeAttribute('src');
      v.load();
      this.preloader.removeAttribute('src');
      this.preloader.load();
      this.stopProgress();
      document.documentElement.classList.remove('ds-lock');
      this.resumeStrip();
    }

    go(i, force) {
      i = Math.max(0, Math.min(this.count - 1, i));
      if (i === this.index && !force) {
        this.positionRail();
        return;
      }
      this.index = i;

      this.slides.forEach((slide, k) => {
        const isActive = k === i;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
        if (Math.abs(k - i) <= 2) {
          const img = slide.querySelector('img[data-src]');
          if (img) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
        }
      });

      this.segs.forEach((seg, k) => {
        seg.firstChild.style.width = k < i ? '100%' : '0%';
      });
      this.counter.textContent = i + 1 + ' / ' + this.count;
      this.msg.textContent = '';
      this.renderCard();
      this.measureCard();
      this.positionRail();

      const v = this.video;
      const item = this.items[i];
      v.pause();
      v.classList.remove('is-ready');
      this.slides[i].appendChild(v);
      if (item.poster) v.poster = item.poster;
      else v.removeAttribute('poster');
      v.src = item.full;
      v.muted = !this.soundOn;
      this.preloadedFor = -1;
      this.play();
    }

    play() {
      const v = this.video;
      const p = v.play();
      if (p && p.catch) {
        p.catch(() => {
          // Browser blocked sound — fall back to muted playback.
          if (!v.muted) {
            v.muted = true;
            this.soundOn = false;
            this.updateSound();
            v.play().catch(() => this.updateTap());
          } else {
            this.updateTap();
          }
        });
      }
    }

    preloadNext() {
      if (this.preloadedFor === this.index) return;
      this.preloadedFor = this.index;
      const next = this.items[this.index + 1];
      if (!next) return;
      setTimeout(() => {
        if (!this.dialog.open || this.preloadedFor !== this.index) return;
        this.preloader.src = next.full;
        this.preloader.load();
      }, 1200);
    }

    // Mobile layout reserves space below the video for the product card.
    measureCard() {
      if (this.card) this.viewport.style.setProperty('--ds-card-h', (this.card.hidden ? 0 : this.card.offsetHeight) + 'px');
    }

    positionRail(drag) {
      const slide = this.slides[this.index];
      if (!slide) return;
      const x = this.viewport.clientWidth / 2 - (slide.offsetLeft + slide.offsetWidth / 2) + (drag || 0);
      this.rail.style.setProperty('--ds-x', x + 'px');
    }

    startProgress() {
      this.stopProgress();
      const tick = () => {
        const v = this.video;
        const seg = this.segs[this.index];
        if (seg && v.duration) {
          seg.firstChild.style.width = (v.currentTime / v.duration) * 100 + '%';
        }
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }

    stopProgress() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    updateTap() {
      const paused = this.video.paused;
      this.tap.classList.toggle('is-paused', paused);
      this.tap.setAttribute('aria-label', paused ? this.tap.dataset.labelPlay : this.tap.dataset.labelPause);
      if (this.pauseBtn) {
        this.pauseBtn.innerHTML = paused ? ICON_PLAY : ICON_PAUSE;
        this.pauseBtn.setAttribute('aria-label', paused ? 'Play' : 'Pause');
      }
    }

    updateSound() {
      this.soundBtn.innerHTML = this.soundOn ? ICON_SOUND_ON : ICON_SOUND_OFF;
      this.soundBtn.setAttribute('aria-label', this.soundOn ? 'Mute' : 'Unmute');
    }

    /* ---------------- Product / variant ---------------- */

    currentProduct() {
      const item = this.items[this.index];
      if (!item || item.product < 0) return null;
      return this.products[item.product] || null;
    }

    currentVariant(product) {
      if (!product) return null;
      const chosen = this.selected[product.id] || product.selected;
      return product.variants.find((v) => v.id === chosen)
        || product.variants.find((v) => v.available)
        || product.variants[0]
        || null;
    }

    /* On a product page, start from the size the shopper picked on the page. */
    syncVariantFromPage() {
      if (this.pageType !== 'product') return;
      const input = document.querySelector('form[action*="/cart/add"] [name="id"]');
      const id = input ? Number(input.value) : Number(new URLSearchParams(window.location.search).get('variant'));
      if (!id) return;
      this.products.forEach((product) => {
        if (product.variants.some((v) => v.id === id)) this.selected[product.id] = id;
      });
    }

    renderCard() {
      const product = this.currentProduct();
      this.card.hidden = !product;
      if (!product) return;

      const v = this.currentVariant(product);

      if (this.cardImg) {
        if (product.image) {
          if (this.cardImg.getAttribute('src') !== product.image) this.cardImg.src = product.image;
          this.cardImg.alt = product.title;
          this.cardImg.hidden = false;
        } else {
          this.cardImg.hidden = true;
        }
      }

      this.cardTitle.textContent = product.title;
      this.cardTitle.href = product.url;

      this.nowEl.textContent = v ? v.price : '';
      this.wasEl.textContent = v && v.compare ? v.compare : '';
      this.wasEl.hidden = !(v && v.compare);
      this.offEl.textContent = v && v.off ? v.off + '% OFF' : '';
      this.offEl.hidden = !(v && v.off);

      // Size chips (rebuilt only when the product changes)
      if (this.variantsEl.dataset.productId !== String(product.id)) {
        this.variantsEl.dataset.productId = String(product.id);
        this.variantsEl.innerHTML = '';
        this.variantsEl.hidden = product.single || product.variants.length < 2;
        this.variantsEl.setAttribute('aria-label', product.option || 'Options');
        if (!this.variantsEl.hidden) {
          product.variants.forEach((variant) => {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'ds-card__chip';
            chip.setAttribute('role', 'radio');
            chip.dataset.variantId = String(variant.id);
            chip.textContent = variant.title;
            this.variantsEl.appendChild(chip);
          });
        }
      }
      this.variantsEl.querySelectorAll('.ds-card__chip').forEach((chip) => {
        chip.setAttribute('aria-checked', String(!!v && Number(chip.dataset.variantId) === v.id));
      });

      if (!this.busy) {
        const available = !!(v && v.available);
        this.buyBtn.disabled = !available;
        this.buyBtn.textContent = available ? this.dataset.buyText : this.dataset.soldText;
      }
    }

    hasShiprocket() {
      const sr = window.shiprocketCheckoutEvents;
      return !!(sr && typeof sr.buyProduct === 'function' && typeof sr.buyCart === 'function');
    }

    /* Hidden product form + Shiprocket button: Shiprocket reads the variant from the button's own form. */
    shiprocketBuyProduct(variantId) {
      let form = this.srForm;
      if (!form || !form.isConnected) {
        form = document.createElement('form');
        form.action = this.dataset.cartAddUrl;
        form.method = 'post';
        form.hidden = true;
        form.className = 'ds-sr-form';
        form.innerHTML =
          '<input type="hidden" name="id" value="">' +
          '<input type="hidden" name="quantity" value="1">' +
          '<button type="button" class="sr-headless-checkout ds-sr-buy" onclick="shiprocketCheckoutEvents.buyProduct(event)" tabindex="-1" aria-hidden="true"></button>';
        document.body.appendChild(form);
        this.srForm = form;
      }
      form.querySelector('[name="id"]').value = String(variantId);
      form.querySelector('.ds-sr-buy').click();
    }

    shiprocketBuyCart() {
      let btn = this.srCartBtn;
      if (!btn || !btn.isConnected) {
        btn = document.createElement('button');
        btn.type = 'button';
        btn.hidden = true;
        btn.className = 'sr-headless-checkout ds-sr-cart';
        btn.setAttribute('onclick', 'shiprocketCheckoutEvents.buyCart(event)');
        btn.setAttribute('tabindex', '-1');
        btn.setAttribute('aria-hidden', 'true');
        document.body.appendChild(btn);
        this.srCartBtn = btn;
      }
      btn.click();
    }

    async addToCartOnce(variantId) {
      const cart = await fetch(this.dataset.cartUrl + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const inCart = Array.isArray(cart.items) && cart.items.some((item) => item.variant_id === variantId);
      if (inCart) return;
      const res = await fetch(this.dataset.cartAddUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || '');
      }
    }

    async buy() {
      const product = this.currentProduct();
      const variant = this.currentVariant(product);
      if (!variant || !variant.available || this.busy) return;

      this.busy = true;
      this.buyBtn.disabled = true;
      this.buyBtn.textContent = this.dataset.loadingText;
      this.msg.textContent = '';

      try {
        if (this.hasShiprocket()) {
          if (this.pageType === 'product') {
            // Product pages: Shiprocket checks out exactly this variant.
            this.dialog.close();
            this.shiprocketBuyProduct(variant.id);
          } else {
            // Other pages: add to cart (no duplicates), then open Shiprocket checkout.
            await this.addToCartOnce(variant.id);
            this.dialog.close();
            this.shiprocketBuyCart();
          }
          this.busy = false;
          this.renderCard();
          return;
        }

        // Shiprocket not available: classic cart flow.
        await this.addToCartOnce(variant.id);
        window.location.href = this.dataset.cartUrl;
      } catch (e) {
        this.msg.textContent = (e && e.message) || this.dataset.errorText;
        this.busy = false;
        this.renderCard();
      }
    }
  }

  customElements.define('dafodila-shoppable', DafodilaShoppable);
})();
